import React, { useEffect, useState } from 'react';
import {
  Button,
  Checkbox,
  Grid,
  MenuItem,
  TextField,
  Typography
} from '@mui/material';
import { LoadingButton } from '@mui/lab';

import { toast } from 'react-toastify';
import { Formik } from 'formik';
import { addSystemUserValidator } from '../../../utils/validators';
import { AddSystemUserProps } from './addSystemUserProps';
import Strings from '../../../i18n';
import { ErrorText } from '../../../components/auth/Profile/completeProfileStyles';
import { usePostUserInfoMutation } from '../../../services/users/usersService';
import {
  useAddSystemRoleWithInfoMutation,
  useAddUpdateSystemUserRoleMutation,
  useGetSystemModulesMutation,
  useGetSystemRolesMutation,
  useUpdateSystemRoleUserMutation
} from '../../../services/systemPermissions/systemPermissionsService';
import {
  StyledContainerFooterSideBar,
  StyledContainerItems,
  StyledEditablePropertyItemContainer,
  StyledTitleSideBar
} from '../../../components/commons/SideBarComponents/sideBarComponentsStyles';
import { SideBarEditablePropertyItem } from '../../../components/commons/SideBarComponents/EditablePropertyItem/sideBarEditablePropertyItem';
import { SideBarPhoneNumber } from '../../../components/commons/SideBarComponents/PhoneNumberWithLada/sideBarPhoneNumber';
import { userRole } from '../../../constants/commonEnums';

export const AddNewSystemUser = ({
  reloadTable,
  setOpenSideBar
}: AddSystemUserProps) => {
  const [roleId, setRoleId] = useState(0);
  const [dataRolesWithAssignLater, setDataRolesWithAssignLater] = useState<
    { id: any; name: any }[]
  >([]);
  const [checked, setChecked] = useState(false);

  const handleChangeCheck = (event: React.ChangeEvent<HTMLInputElement>) => {
    setChecked(event.target.checked);
  };

  const [
    getSystemRoles,
    { isSuccess: successGetSystemRoles, data: dataSystemRoles }
  ] = useGetSystemRolesMutation();

  const [
    addSystemRole,
    { isSuccess: isSuccessAddSystemRole, data: dataAddSystemRole, isLoading }
  ] = useAddUpdateSystemUserRoleMutation();

  const [
    postUserInfo,
    { isLoading: isLoadingPostInfo, isSuccess: isSuccessPostInfo, data: user }
  ] = usePostUserInfoMutation();

  const sendInfo = async (payload: any) => {
    postUserInfo(payload);
  };

  useEffect(() => {
    if (successGetSystemRoles && dataSystemRoles) {
      const roles = [
        { id: 0, name: Strings.common.assignLater },
        ...dataSystemRoles
      ];
      setDataRolesWithAssignLater(roles);
    }
  }, [successGetSystemRoles]);

  useEffect(() => {
    getSystemRoles('');
  }, []);
  useEffect(() => {
    if (isSuccessPostInfo && user) {
      if (checked) {
        addSystemRole({
          userId: user.userId,
          systemRoleId: userRole.admin
        });
      } else {
        toast.success(Strings.users.successCreateUser);
        reloadTable();
        setOpenSideBar(false);
      }
    }
  }, [isSuccessPostInfo, user]);

  useEffect(() => {
    if (isSuccessAddSystemRole) {
      toast.success(Strings.users.successCreateUser);
      reloadTable();
      setOpenSideBar(false);
    }
  }, [isSuccessAddSystemRole]);

  return (
    <Formik
      enableReinitialize
      validationSchema={addSystemUserValidator}
      initialValues={{
        email: '',
        firstName: '',
        lastName: '',
        phoneNumber: null,
        password: ''
      }}
      onSubmit={(values) => {
        sendInfo({
          email: values.email,
          fullName: `${values.firstName} ${values.lastName}`,
          firstName: values.firstName,
          lastName: values.lastName,
          auth0Id: '',
          phoneNumber: values.phoneNumber ?? 0,
          password: values.password
        });
      }}
    >
      {({
        handleChange,
        handleSubmit,
        values,
        isValid,
        setFieldTouched,
        errors,
        touched
      }) => (
        <>
          <StyledContainerItems>
            <StyledTitleSideBar>
              {Strings.users.createNewUser}
            </StyledTitleSideBar>
            <>
              <SideBarEditablePropertyItem
                item={{
                  label: Strings.userProfile.firstNameLabel,
                  type: 'inputText',
                  currentValue: values.firstName,
                  nameAndId: 'firstName',
                  onChange: handleChange('firstName'),
                  onBlur: () => setFieldTouched('firstName')
                }}
              />
              {touched.firstName && errors.firstName && (
                <ErrorText>{errors.firstName}</ErrorText>
              )}
              <SideBarEditablePropertyItem
                item={{
                  label: Strings.userProfile.lastNameLabel,
                  type: 'inputText',
                  currentValue: values.lastName,
                  nameAndId: 'lastName',
                  onChange: handleChange('lastName'),
                  onBlur: () => setFieldTouched('lastName')
                }}
              />
              {touched.lastName && errors.lastName && (
                <ErrorText>{errors.lastName}</ErrorText>
              )}
              <SideBarEditablePropertyItem
                item={{
                  label: Strings.userProfile.emailLabel,
                  type: 'inputText',
                  currentValue: values.email,
                  nameAndId: 'email',
                  onChange: handleChange('email'),
                  onBlur: () => setFieldTouched('email')
                }}
              />
              {touched.email && errors.email && (
                <ErrorText>{errors.email}</ErrorText>
              )}
              <SideBarPhoneNumber
                value={values.phoneNumber}
                handleChange={(value) =>
                  handleChange('phoneNumber')(value ?? '')
                }
                handleBlur={() => setFieldTouched('phoneNumber')}
                id="phoneNumber"
              />
              {touched.phoneNumber && errors.phoneNumber && (
                <ErrorText>{errors.phoneNumber}</ErrorText>
              )}
              <SideBarEditablePropertyItem
                item={{
                  label: Strings.userProfile.passwordLabel,
                  type: 'inputText',
                  currentValue: values.password,
                  placeholder: '',
                  nameAndId: 'password',
                  onBlur: () => setFieldTouched('password'),
                  onChange: handleChange('password')
                }}
              />
              {touched.password && errors.password && (
                <ErrorText>{errors.password}</ErrorText>
              )}
            </>
            <StyledEditablePropertyItemContainer>
              <div className="label">{Strings.users.fullAccessServer}</div>
              <Checkbox
                checked={checked}
                onChange={handleChangeCheck}
                color="secondary"
              />
            </StyledEditablePropertyItemContainer>
          </StyledContainerItems>

          <StyledContainerFooterSideBar>
            <button
              type="button"
              className="btn-cancel"
              onClick={() => {
                setOpenSideBar(false);
              }}
            >
              {Strings.common.cancel}
            </button>
            <LoadingButton
              variant="contained"
              disabled={!isValid}
              size="large"
              className="btn-save"
              onClick={() => handleSubmit()}
              loading={isLoadingPostInfo || isLoading}
            >
              {Strings.common.save}
            </LoadingButton>
          </StyledContainerFooterSideBar>
        </>
      )}
    </Formik>
  );
};
